import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useSEO } from "../lib/seo";
import ComebackBanner from "../components/ComebackBanner";
import { Flame, Trophy, TrendingUp, BookOpen, Target, ArrowRight, Sparkles, Award, Play, RotateCcw } from "lucide-react";

function Stat({ label, value, color = "text-foreground" }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="text-[11px] uppercase font-bold tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={`mt-2 font-display text-3xl tracking-tight ${color}`}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/me/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useSEO({ title: "Your dashboard", noindex: true });

  if (!data) return <div className="container-x py-16" data-testid="dashboard-loading">Loading your dashboard…</div>;

  const trendMax = Math.max(1, ...data.trend.map((d) => d.score));
  const resumeAttempt = data.resume_attempts?.[0];
  const continueCourse = data.enrolled?.[0];
  const mistakeCourseId = data.mistake_bank?.[0]?.course_id;

  return (
    <div className="container-x py-10" data-testid="dashboard-page">
      <ComebackBanner user={data.user} attempts={data.recent_attempts || []} />
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase font-bold tracking-[0.18em] text-[color:var(--brand-blue)]">Welcome back</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl tracking-tight">
            {user?.name?.split(" ")[0] || "Student"} — your prep dashboard
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {(resumeAttempt || continueCourse) && (
            <Link to={resumeAttempt ? `/practice/${resumeAttempt.course_id}/${resumeAttempt.test_number}?mode=${resumeAttempt.mode}` : `/practice/${continueCourse.id}/1?mode=practice`}>
              <button className="btn-primary" data-testid="continue-practice">
                <Play className="h-4 w-4" /> {resumeAttempt ? "Resume attempt" : "Continue practice"}
              </button>
            </Link>
          )}
          {mistakeCourseId && (
            <Link to={`/practice/${mistakeCourseId}/1?mode=mistakes`}>
              <button className="btn-ghost" data-testid="start-mistake-drill">
                <RotateCcw className="h-4 w-4" /> Drill mistakes
              </button>
            </Link>
          )}
          <Link to="/marketplace">
            <button className="btn-ghost" data-testid="dashboard-browse">
              Browse practice tests <ArrowRight className="h-4 w-4" />
            </button>
          </Link>
        </div>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="text-[11px] uppercase font-bold tracking-[0.18em] text-muted-foreground">Current streak</div>
          <div className="mt-2 flex items-center gap-2">
            <Flame className="h-7 w-7 text-[color:var(--brand-flame)] flame-anim" />
            <span className="font-display text-3xl">{data.user.streak} days</span>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="text-[11px] uppercase font-bold tracking-[0.18em] text-muted-foreground">Total XP</div>
          <div className="mt-2 flex items-center gap-2">
            <Trophy className="h-7 w-7 text-yellow-500" />
            <span className="font-display text-3xl">{data.user.xp}</span>
          </div>
        </div>
        <Stat label="Readiness" value={`${data.stats.readiness}%`} color="text-[color:var(--brand-blue)]" />
        <Stat label="Tests passed" value={`${data.stats.passed} / ${data.stats.tests_taken}`} />
        <Stat label="Open mistakes" value={data.stats.open_mistakes || 0} color="text-[color:var(--brand-flame)]" />
      </div>

      {resumeAttempt && (
        <div className="mt-8 rounded-xl border border-amber-400/30 bg-amber-400/10 p-5 flex flex-wrap items-center gap-4" data-testid="resume-attempt-card">
          <div className="flex-1 min-w-[220px]">
            <div className="text-xs uppercase font-bold tracking-[0.18em] text-[color:var(--brand-amber-hover)]">Saved progress</div>
            <div className="font-display text-xl mt-1">Resume {resumeAttempt.course?.title || "your practice test"}</div>
            <div className="text-sm text-muted-foreground mt-1">
              {Object.keys(resumeAttempt.answers || {}).length}/{resumeAttempt.question_ids?.length || 0} answered · {resumeAttempt.mode} mode
            </div>
          </div>
          <Link to={`/practice/${resumeAttempt.course_id}/${resumeAttempt.test_number}?mode=${resumeAttempt.mode}`}>
            <button className="btn-primary" data-testid="resume-attempt-button">Resume now <ArrowRight className="h-4 w-4" /></button>
          </Link>
        </div>
      )}

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        {/* Continue / Enrolled */}
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl">My practice tests</h2>
            <Link to="/marketplace" className="text-xs font-semibold text-muted-foreground hover:text-foreground">Browse →</Link>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            {data.enrolled.length === 0 && (
              <div className="col-span-full rounded-lg border border-dashed border-border p-8 text-center">
                <BookOpen className="h-8 w-8 mx-auto text-muted-foreground" />
                <h3 className="mt-3 font-display text-lg">No courses yet</h3>
                <p className="text-sm text-muted-foreground mt-1">Enroll in your first practice test to start a streak.</p>
                <Link to="/marketplace" className="inline-flex mt-4 btn-primary">Browse marketplace</Link>
              </div>
            )}
            {data.enrolled.map((c) => (
              <Link key={c.id} to={`/practice/${c.id}/1`} className="rounded-lg border border-border p-4 hover:bg-accent flex gap-3 items-center" data-testid={`enrolled-${c.slug}`}>
                <img src={c.image_url} alt="" className="h-14 w-20 rounded object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-sm line-clamp-1">{c.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{c.test_count} mocks · {c.question_count} questions</div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            ))}
          </div>

          {/* Trend */}
          <div className="mt-8">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="h-4 w-4 text-[color:var(--brand-blue)]" />
              <h3 className="font-display text-lg">Last 7 days</h3>
            </div>
            <div className="flex items-end gap-2 h-32">
              {data.trend.map((t) => (
                <div key={t.date} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className="w-full rounded-t-md bg-[color:var(--brand-blue)]/20 border-t-2 border-[color:var(--brand-blue)]"
                    style={{ height: `${(t.score / trendMax) * 100}%`, minHeight: t.score ? 4 : 0 }}
                  />
                  <div className="text-[10px] text-muted-foreground">{t.date.slice(5)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Side: weak topics + badges */}
        <div className="space-y-6">
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="flex items-center gap-2 mb-3">
              <Target className="h-4 w-4 text-[color:var(--brand-flame)]" />
              <h3 className="font-display text-lg">Weak topics</h3>
            </div>
            {data.weak_topics.length === 0 ? (
              <p className="text-sm text-muted-foreground">Take a few attempts to unlock weak-topic radar.</p>
            ) : (
              <ul className="space-y-3">
                {data.weak_topics.map((t) => (
                  <li key={t.topic}>
                    <div className="flex justify-between text-sm">
                      <span className="font-semibold">{t.topic}</span>
                      <span className="text-muted-foreground">{t.score}%</span>
                    </div>
                    <div className="mt-1 h-1.5 w-full rounded bg-muted overflow-hidden">
                      <div className="h-full bg-[color:var(--brand-flame)]" style={{ width: `${t.score}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-6" data-testid="mistake-bank-card">
            <div className="flex items-center gap-2 mb-3">
              <RotateCcw className="h-4 w-4 text-[color:var(--brand-flame)]" />
              <h3 className="font-display text-lg">Mistake bank</h3>
            </div>
            {(data.mistake_bank || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Wrong answers will appear here for focused retakes.</p>
            ) : (
              <div className="space-y-3">
                {data.mistake_bank.slice(0, 4).map((m) => (
                  <div key={m.question_id} className="rounded-md border border-border p-3" data-testid={`mistake-${m.question_id}`}>
                    <div className="text-xs text-muted-foreground">{m.topic} · missed {m.wrong_count || 1}×</div>
                    <div className="mt-1 text-sm font-semibold line-clamp-2">{m.text}</div>
                  </div>
                ))}
                {mistakeCourseId && (
                  <Link to={`/practice/${mistakeCourseId}/1?mode=mistakes`} className="inline-flex btn-primary mt-1" data-testid="mistake-bank-drill-link">
                    Drill now <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-6">
            <div className="flex items-center gap-2 mb-3">
              <Award className="h-4 w-4 text-yellow-500" />
              <h3 className="font-display text-lg">Badges</h3>
            </div>
            {(data.user.badges || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Complete a mock test to earn your first badge.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {data.user.badges.map((b) => (
                  <span key={b} className="rounded-full bg-yellow-400/15 text-yellow-700 dark:text-yellow-300 px-2.5 py-1 text-xs font-semibold">
                    🏆 {b}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-[color:var(--brand-blue)]" />
              <h3 className="font-display text-lg">Recommended for you</h3>
            </div>
            <div className="space-y-3">
              {data.recommendations.slice(0, 3).map((c) => (
                <Link key={c.id} to={`/courses/${c.slug}`} className="flex gap-3 items-center hover:bg-accent rounded-md p-2 -mx-2">
                  <img src={c.image_url} alt="" className="h-10 w-14 rounded object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold line-clamp-1">{c.title}</div>
                    <div className="text-xs text-muted-foreground">${c.price.toFixed(2)} · {c.test_count} mocks</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
